'use client';
import {
  refreshToken,
  removeCredentials,
} from '@/services/AuthorizationService';
import { RootState } from '@/store/store';
import {
  AccountStatus,
  IEmployee,
  IEmployer,
  INumeroAgrement,
  IRetail,
  ImportPdvResponse,
  ImportResponse,
  QueryReturnValue,
} from '@/types';
import getEnv from '@/utils/env';
import {
  BaseQueryApi,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

export interface ReqModifierPDV {
  siren: string;
  posid: string;
  reqbody: { name: string, partner_pos_id: string };
}

export interface PdvPayload {
  bnppf_pos_id: string;
  name: string;
  partner_pos_id: string;
  business_domain: string;
  employer: string;
}
export interface ReqAjouterPDV {
  siren: string;
  reqbody: PdvPayload[];
}

export interface RespAjouterPDV {
  id: string;
  message: string;
  description: string;
}

export interface GetRetailersQuery {
  num: string;
  siren: string;
}

export interface DeletePDVresp {
  id: string;
  message: string;
  description: string;
}

export interface DeleteEmpResp {
  id: string;
  message: string;
  description: string;
}

export interface DeletePDVreq {
  id: string;
}
export interface DeleteEmpReq {
  id: string;
}

export type BodyEditEmployee = Pick<
  IEmployee,
  | 'business_domain'
  | 'fin_ent'
  | 'employer'
  | 'given_name'
  | 'sn'
  | 'email_address'
  | 'mobile'
  | 'account_status'
  | 'federation_id'
  | 'supervisor'
  | 'mfa_capable'
  | 'last_login_time'
  | 'operational_seller_number_list'
  | 'administrator_type'
  | 'administrative_seller_number_list'
>;

export type BodyEditEmployer = Pick<
  IEmployer,
  'tag' | 'employer_type' | 'api_organization_id' | 'authn_method'
>;

export interface AddEmpReq {
  sn: string;
  mobile: string;
  given_name: string;
  email_address: string;
  business_domain: 'DISTRIB';
  operational_seller_number_list: string[];
  administrator_type: string;
  administrative_seller_number_list: string[];
  account_status: string;
  mfa_capable: boolean;
  supervisor: boolean;
  fin_ent: string;
  employer: string;
  federation_id: string;
}

export interface AddSteReq {
  siren: string;
  name: string;
  tag: string;
  employer_type: string;
  api_organization_id: string;
  authn_method: string;
  fin_ent?: string;
}

export type FormEmployee = Pick<
  IEmployee,
  | 'given_name'
  | 'sn'
  | 'email_address'
  | 'mobile'
  | 'account_status'
  | 'federation_id'
  | 'mfa_capable'
  | 'supervisor'
>;

export type AdminFormModel = Pick<
  IEmployee,
  | 'given_name'
  | 'sn'
  | 'email_address'
  | 'mobile'
  | 'federation_id'
  | 'mfa_capable'
  | 'administrator_type'
  | 'account_status'
>;

export interface ChangeEmpStatusReq {
  employee_id: string;
  status: AccountStatus;
}

export interface ChangeEmpStatusResp {
  id: string;
  message: string;
  description: string;
}

const getAuthorization = async () => {
  const expiresAt = sessionStorage.getItem('expiresAt');
  const profile = getEnv().PROFIL;
  if (
    profile === 'PF' &&
    expiresAt &&
    Number(expiresAt) < Math.floor(Date.now() / 1000)
  ) {
    return refreshToken()
      .then((result) => {
        return `Bearer ${result}`;
      })
      .catch(() => {
        return `Bearer `;
      });
  } else return `Bearer ${sessionStorage.getItem('accessToken')}`;
};

const getBaseQuery =
  () =>
  async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: {}) => {
    const result = await fetchBaseQuery({
      baseUrl: getEnv().msHostname,
      headers: {
        Authorization: await getAuthorization(),
      },
    })(args, api, extraOptions);
    if (result?.error?.status === 401) {
      sessionStorage.setItem('lastURL', window.location.pathname);
      removeCredentials(api.dispatch);
    }
    return result;
  };

export const ppidApi = createApi({
  reducerPath: 'ppidApi',
  refetchOnMountOrArgChange: true,
  baseQuery: getBaseQuery(),
  endpoints: (builder) => ({
    getListEmployers: builder.query<IEmployer[], void>({
      query: () => '/api/v1/employers',
    }),
    getEmployer: builder.query<IEmployer, string>({
      query: (siren) => `/api/v1/employers?siren=${siren}`,
      transformResponse: (response: IEmployer[]) => response[0],
    }),
    editEmployer: builder.mutation<
      IEmployer,
      { id: string; body: BodyEditEmployer }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1/employers/${id}`,
        method: 'PUT',
        body: body,
      }),
    }),
    getListEmployees: builder.query<IEmployee[], string>({
      query: (siren) => '/api/v1/employers/' + siren + '/employees',
    }),
    getEmployee: builder.query<
      IEmployee,
      { idEmployee: string; siren: string }
    >({
      query: ({ siren, idEmployee }) => {
        return `/api/v1/employers/${siren}/employees/${idEmployee}`;
      },
    }),
    editEmployee: builder.mutation<
      IEmployee,
      { siren: string; idEmployee: string; body: BodyEditEmployee }
    >({
      query: ({ siren, idEmployee, body }) => ({
        url: `/api/v1/employers/${siren}/employees/${idEmployee}`,
        method: 'PUT',
        body: body,
      }),
    }),
    getListPdv: builder.query<INumeroAgrement[], string>({
      query: (siren) => '/api/v1/employers/' + siren + '/pointOfSales',
    }),
    getListRetailers: builder.query<IRetail[], GetRetailersQuery>({
      query: ({ num, siren }) =>
        '/api/v1/employers/' + siren + '/retailers?number=' + num,
    }),
    getPdv: builder.query<INumeroAgrement, { idPdv: string; siren: string }>({
      query: ({ siren, idPdv }) => {
        return `/api/v1/employers/${siren}/pointOfSales/${idPdv}`;
      },
    }),
    getListAdmins: builder.query<IEmployee[], string>({
      query: (siren) => '/api/v1/employers/' + siren + '/administrators',
    }),

    editPdv: builder.mutation<INumeroAgrement, ReqModifierPDV>({
      query: (req: ReqModifierPDV) => ({
        url: '/api/v1/employers/' + req.siren + '/pointOfSales/' + req.posid,
        method: 'PATCH',
        body: req.reqbody,
      }),
    }),
    addPdv: builder.mutation<{ resources: RespAjouterPDV[] }, ReqAjouterPDV>({
      query: (req: ReqAjouterPDV) => ({
        url: '/api/v1/employers/' + req.siren + '/pointOfSales',
        method: 'POST',
        body: { resources: req.reqbody },
      }),
    }),
    deletePdv: builder.mutation<
      { resources: DeletePDVresp[] },
      { siren: string; resources: DeletePDVreq[] }
    >({
      query: (req: { siren: string; resources: DeletePDVreq[] }) => ({
        url: '/api/v1/employers/' + req.siren + '/pointOfSales',
        method: 'DELETE',
        body: { resources: req.resources },
      }),
    }),
    addEmp: builder.mutation<
      IEmployee & { user_password: string },
      { siren: string; reqBody: AddEmpReq }
    >({
      query: (req: { siren: string; reqBody: AddEmpReq }) => ({
        url: '/api/v1/employers/' + req.siren + '/employees',
        method: 'POST',
        body: req.reqBody,
      }),
    }),
    deleteEmp: builder.mutation<
      { resources: DeleteEmpResp[] },
      { siren: string; resources: DeleteEmpReq[] }
    >({
      query: (req: { siren: string; resources: DeleteEmpReq[] }) => ({
        url: '/api/v1/employers/' + req.siren + '/employees',
        method: 'DELETE',
        body: { resources: req.resources },
      }),
    }),
    resetPassword: builder.mutation<
      IEmployee & { user_password: string },
      { siren: string; empId: string }
    >({
      query: (req: { siren: string; empId: string }) => ({
        url:
          '/api/v1/employers/' +
          req.siren +
          '/employees/' +
          req.empId +
          '/resetPassword',
        method: 'POST',
      }),
    }),
    addSte: builder.mutation<IEmployer, AddSteReq>({
      query: (req: AddSteReq) => ({
        url: '/api/v1/employers',
        method: 'POST',
        body: req,
      }),
    }),
    importEmployees: builder.mutation<
      {
        resources: ImportResponse[];
      },
      FormData
    >({
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const siren: string = (api.getState() as RootState).selectedSte.societe
          .siren;
        const result = await baseQuery({
          url: `/api/v1/employers/${siren}/employees/import`,
          method: 'POST',
          body: arg,
        });
        return result as QueryReturnValue<
          {
            resources: ImportResponse[];
          },
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;
      },
    }),
    changeEmpStatus: builder.mutation<
      { resources: ChangeEmpStatusResp[] },
      { siren: string; resources: ChangeEmpStatusReq[] }
    >({
      query: (req: { siren: string; resources: ChangeEmpStatusReq[] }) => ({
        url: '/api/v1/employers/' + req.siren + '/employees',
        method: 'PATCH',
        body: { resources: req.resources },
      }),
    }),
    importPdvs: builder.mutation<
      {
        resources: ImportPdvResponse[];
      },
      FormData
    >({
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const siren: string = (api.getState() as RootState).selectedSte.societe
          .siren;
        const result = await baseQuery({
          url: `/api/v1/employers/${siren}/pointOfSales/import`,
          method: 'POST',
          body: arg,
        });
        return result as QueryReturnValue<
          {
            resources: any[];
          },
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;
      },
    }),
    exportEmployees: builder.mutation<any, void>({
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const siren: string = (api.getState() as RootState).selectedSte.societe
          .siren;
        const result = await baseQuery({
          url: `/api/v1/employers/${siren}/employees/export`,
          method: 'GET',
          responseHandler: 'text',
        });
        return result;
      },
    }),
    exportPdvs: builder.mutation<any, void>({
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const siren: string = (api.getState() as RootState).selectedSte.societe
          .siren;
        const result = await baseQuery({
          url: `/api/v1/employers/${siren}/pointOfSales/export`,
          method: 'GET',
          responseHandler: 'text',
        });
        return result;
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetListEmployersQuery,
  useLazyGetListEmployersQuery,
  useGetListEmployeesQuery,
  useLazyGetListEmployeesQuery,
  useGetListPdvQuery,
  useLazyGetListPdvQuery,
  useGetListAdminsQuery,
  useLazyGetListAdminsQuery,
  useGetEmployeeQuery,
  useEditEmployeeMutation,
  useGetListRetailersQuery,
  useEditPdvMutation,
  useAddPdvMutation,
  useDeletePdvMutation,
  useAddEmpMutation,
  useDeleteEmpMutation,
  useResetPasswordMutation,
  useAddSteMutation,
  useGetEmployerQuery,
  useLazyGetEmployerQuery,
  useEditEmployerMutation,
  useGetPdvQuery,
  useImportEmployeesMutation,
  useChangeEmpStatusMutation,
  useImportPdvsMutation,
  useExportEmployeesMutation,
  useExportPdvsMutation,
} = ppidApi;
